import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

/* Jede Kasse gehoert auf ihr EIGENES Sachkonto: eine Firma kann eine
   Ladenkasse und eine Portokasse fuehren, und beide auf dasselbe Konto zu
   legen macht ihre Salden ununterscheidbar. */
const empty = () => ({ code: '', name: '', currency: 'CHF', accountId: '' });

export function CashJournalModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);

  /* Nur Aktivkonten kommen als Kassenkonto in Frage - Bargeld ist ein
     Vermoegenswert. Ein Aufwandkonto hier waere nicht falsch getippt, sondern
     falsch gedacht, und die Bilanz ginge trotzdem auf. */
  const kontenQuery = useQuery({
    queryKey: ['finance', 'accounts', 'aktiv'],
    queryFn: () => financeApi.listAccounts({ type: 'ASSET', limit: 300 }).then((r) => r.items || []),
    enabled: open,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.createCashJournal(body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'cash-journals'] });
      if (res?.data?.id) onCreated?.(res.data.id);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.code.trim() || !form.name.trim()) { setError(t('cb.error.journalRequired')); return; }
    save.mutate({
      code: form.code.trim(),
      name: form.name.trim(),
      currency: form.currency || 'CHF',
      ...(form.accountId ? { accountId: form.accountId } : {}),
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('cb.journal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field" style={{ maxWidth: 150 }}><span className="field-label">{t('cb.field.code')}</span>
            <input className="input mono" value={form.code} onChange={set('code')} placeholder="KASSE" required />
          </label>
          <label className="field"><span className="field-label">{t('cb.field.name')}</span>
            <input className="input" value={form.name} onChange={set('name')} required />
          </label>
          <label className="field" style={{ maxWidth: 100 }}><span className="field-label">{t('cb.field.currency')}</span>
            <input className="input mono" value={form.currency} onChange={set('currency')} maxLength={3} />
          </label>
        </div>
        <label className="field"><span className="field-label">{t('cb.field.account')}</span>
          <select className="input" value={form.accountId} onChange={set('accountId')}>
            <option value="">{t('cb.field.accountDefault')}</option>
            {(kontenQuery.data || []).map((k) => (
              <option key={k.id} value={k.id}>{`${k.code} ${k.name}`}</option>
            ))}
          </select>
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
